import type { AEInfo, EditableTypeType } from "hoxton-types";

import type { InputAEComposition, ComputedOverwrite, PlainlyOutputOptions } from "@/types/types";
import { NotificationTypes } from "@/enums/notifications";

import * as ciApi from "./CreativeIntelligenceApi";
import aeHandleErrors from "./aeHandleErrors";
import { createJob } from "./CreativeIntelligenceApi/jobs";

export const PLAINLY_POLL_INTERVAL = 2000;
/**
 * we want to ignore the master template assets as the url is incorrect
 * plainly will rely on the default assets if we don't send a computed layer at all
 */
function isMasterTemplateAsset(url: string): boolean {
    return url.includes("/master-templates/");
}

function parseAssetUrl(url: string): [bucket: string, key: string] {
    return url.split("?")[0]?.replace("https://", "").split(".s3.eu-west-1.amazonaws.com/") as [string, string];
}

export function getPlainlyComputedLayers(overwrites: ComputedOverwrite[]): ciApi.PlainlyComputedLayer[] {
    const init: ciApi.PlainlyComputedLayer[] = [];

    return overwrites.reduce((acc, { newValue, name, type, isPublisherPlatform, layerManagement }) => {
        if (
            newValue !== undefined &&
            newValue !== null &&
            newValue !== "" &&
            !isMasterTemplateAsset(newValue) &&
            !isPublisherPlatform
        ) {
            if (type === "image" || type === "video") {
                const [bucket, key] = parseAssetUrl(newValue);

                acc.push({
                    name,
                    fileLocation: {
                        bucket,
                        key
                    }
                });
            } else {
                acc.push({
                    name,
                    value: newValue,
                    layerManagement
                });
            }
        }

        return acc;
    }, init);
}

export interface RequestRenderInput {
    reportingLabel: string;
    aeInfo: AEInfo;
    computedOverwrites: ComputedOverwrite[];
    preview?: boolean;
}

interface AERenderServiceInstance {
    clear: () => void;
    requestRender: (input: RequestRenderInput) => Promise<string | null>;
}

export function AERenderService({ clientId }: { clientId: string }): AERenderServiceInstance {
    if (!clientId) {
        throw new Error("Invalid AE info");
    }

    const queue = new Map<symbol, string>();

    function clear(): void {
        queue.clear();
    }

    function requestRender({
        reportingLabel,
        aeInfo: { completed, plainlyProjectId, plainlyTemplateId },
        computedOverwrites,
        preview = false
    }: RequestRenderInput) {
        return new Promise<string | null>((resolve, reject) => {
            if (!completed || !plainlyProjectId || !plainlyTemplateId || !Array.isArray(computedOverwrites)) {
                reject(new Error("Invalid AE info"));

                return;
            }

            const computedLayers = getPlainlyComputedLayers(computedOverwrites);

            queue.forEach((label, requestId) => {
                if (label === reportingLabel) {
                    queue.delete(requestId);
                }
            });

            const requestId = Symbol(reportingLabel);
            queue.set(requestId, reportingLabel);

            async function pollApi() {
                if (!queue.has(requestId)) {
                    resolve(null);

                    return;
                }

                const { data } = await ciApi.createPlainlyRender({
                    id: clientId,
                    input: {
                        reportingLabel,
                        plainlyProjectId,
                        plainlyTemplateId,
                        outputOptions: {
                            preview
                        },
                        computedLayers
                    }
                });

                switch (data.status) {
                    case ciApi.RESOURCE_MANAGER_STATUS.IN_PROGRESS:
                        // eslint-disable-next-line @typescript-eslint/no-misused-promises
                        setTimeout(pollApi, 2000);

                        return;

                    case ciApi.RESOURCE_MANAGER_STATUS.AVAILABLE:
                        queue.delete(requestId);

                        resolve(data.output.success.fileLocation.signedUrl);

                        return;

                    case ciApi.RESOURCE_MANAGER_STATUS.FAILED:
                    default:
                        queue.delete(requestId);

                        reject(new Error(aeHandleErrors(data.output.fail)));
                }
            }

            void pollApi();
        });
    }

    return {
        clear,
        requestRender
    };
}

export async function createAEJob({
    inputs,
    user,
    campaignId,
    output
}: {
    inputs: RequestRenderInput[];
    user: { name: string; id: string };
    campaignId: string;
    output: Omit<PlainlyOutputOptions, "preview">;
}) {
    const body = {
        type: NotificationTypes.PlainlyBatchRender,
        input: {
            recipients: [],
            campaignId,
            itemsToRender: inputs.map(
                ({ reportingLabel, aeInfo: { plainlyProjectId, plainlyTemplateId }, computedOverwrites }) => {
                    const computedLayers = getPlainlyComputedLayers(computedOverwrites);

                    return {
                        reportingLabel,
                        plainlyProjectId,
                        plainlyTemplateId,
                        outputOptions: {
                            preview: false,
                            ...output
                        },
                        computedLayers,
                        campaignId
                    };
                }
            )
        },
        user
    };

    const { data } = await createJob({ body });

    return data;
}

export function requestBatchRender({ clientId, inputs }: { clientId: string; inputs: RequestRenderInput[] }) {
    return new Promise((resolve, reject) => {
        if (!clientId) {
            reject(new Error("Invalid AE info"));

            return;
        }

        inputs.forEach(({ aeInfo: { completed, plainlyProjectId, plainlyTemplateId }, computedOverwrites }) => {
            if (!completed || !plainlyProjectId || !plainlyTemplateId || !Array.isArray(computedOverwrites)) {
                reject(new Error("Invalid AE info"));
            }
        });

        async function pollApi(): Promise<void> {
            const { data } = await ciApi.createPlainlyBatchRender({
                id: clientId,
                inputs: inputs.map(
                    ({ reportingLabel, aeInfo: { plainlyProjectId, plainlyTemplateId }, computedOverwrites }) => {
                        const computedLayers = getPlainlyComputedLayers(computedOverwrites);

                        return {
                            reportingLabel,
                            plainlyProjectId,
                            plainlyTemplateId,
                            outputOptions: {
                                preview: false
                            },
                            computedLayers
                        };
                    }
                )
            });

            switch (data.status) {
                case ciApi.RESOURCE_MANAGER_STATUS.IN_PROGRESS:
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    setTimeout(pollApi, PLAINLY_POLL_INTERVAL);

                    return;

                case ciApi.RESOURCE_MANAGER_STATUS.AVAILABLE:
                    resolve(data.output.success.zipFileLocation.signedUrl);

                    return;

                case ciApi.RESOURCE_MANAGER_STATUS.FAILED:
                default:
                    reject(new Error("There was an unexpected error while rendering the AE template"));
            }
        }

        void pollApi();
    });
}

/**
 * CREATE PLAINLY PROJECT
 */
function getDynamicChildrenLayers(children: ciApi.ChildLayer[]): ciApi.ChildLayer[] {
    return children.reduce<ciApi.ChildLayer[]>((acc, layer) => {
        if (layer.name.startsWith("$")) {
            acc.push(layer);
        }

        if (layer.children?.length) {
            return acc.concat(getDynamicChildrenLayers(layer.children));
        }

        return acc;
    }, []);
}

function mapAELayerTypeToEditableType(type: string, mediaType?: string): EditableTypeType {
    switch (type) {
        case "MEDIA":
            if (mediaType === "IMAGE") {
                return "image";
            }

            if (mediaType === "VIDEO") {
                return "video";
            }

            return "file";

        case "TEXT":
            return "text";

        default:
            return "file";
    }
}

async function getLayers(url: string) {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error("Failed to fetch the layers file");
    }

    return response.json() as Promise<ciApi.CompositionLayer[]>;
}

async function createPlainlyTemplates(
    resourceId: string,
    layers: ciApi.CompositionLayer[]
): Promise<InputAEComposition[]> {
    interface CompositionTemplate {
        compositionLayer: ciApi.CompositionLayer;
        childLayers: ciApi.ChildLayer[];
    }

    const compositionTemplates = layers.reduce<CompositionTemplate[]>((acc, layer) => {
        if (layer.type === "COMPOSITION" && layer.name.startsWith("$") && layer.children.length) {
            const childLayers = getDynamicChildrenLayers(layer.children);

            acc.push({
                compositionLayer: layer,
                childLayers
            });
        }

        return acc;
    }, []);

    const plainlyTemplates = compositionTemplates.map(({ compositionLayer, childLayers }) => {
        const plainlyChildLayers = childLayers.map(childLayer => {
            const [, first, second] = childLayer.name.split("$");

            return {
                id: childLayer.internalId,
                name: second ?? first ?? ""
            };
        });

        return {
            name: compositionLayer.name.split("$")[1] ?? "",
            renderingCompositionId: Number(compositionLayer.internalId),
            layers: plainlyChildLayers
        };
    });

    let plainlyTemplateIds: string[] = [];

    try {
        const response = await ciApi.createPlainlyTemplates({
            resourceId,
            templates: plainlyTemplates
        });

        plainlyTemplateIds = response.data;
    } catch {
        return [];
    }

    return compositionTemplates.map(({ compositionLayer, childLayers }, index) => {
        const layers = childLayers.map(childLayer => {
            const [, first, second] = childLayer.name.split("$");

            const name = second ?? first ?? "";
            const group = second ? (first ?? "") : "generic";

            const editableType = mapAELayerTypeToEditableType(childLayer.type, childLayer.mediaType);

            return {
                id: childLayer.internalId,
                name,
                label: name,
                group,
                type: editableType,
                // we ignore the project's internal file path values for images and videos
                defaultValue: editableType === "text" ? childLayer.value : ""
            };
        });

        return {
            name: compositionLayer.name.split("$")[1] ?? "",
            width: compositionLayer.width,
            height: compositionLayer.height,
            plainlyTemplateId: plainlyTemplateIds[index] ?? "",
            layers
        };
    });
}

export function createAEProject({
    clientId,
    persistentBucket,
    key
}: {
    clientId: string;
    persistentBucket: string;
    key: string;
}): Promise<{ plainlyProjectId: string; compositions: InputAEComposition[] }> {
    return new Promise((resolve, reject) => {
        if (!clientId || !persistentBucket || !key) {
            reject(new Error("Invalid AE info"));

            return;
        }

        async function pollApi() {
            const { data } = await ciApi.createPlainlyProject({
                id: clientId,
                bucket: persistentBucket,
                key
            });

            switch (data.status) {
                case ciApi.RESOURCE_MANAGER_STATUS.IN_PROGRESS:
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    setTimeout(pollApi, PLAINLY_POLL_INTERVAL);

                    return;

                case ciApi.RESOURCE_MANAGER_STATUS.AVAILABLE: {
                    const layers = await getLayers(data.output.success.layersFileLocation.signedUrl);
                    const compositions = await createPlainlyTemplates(data.resourceId, layers);

                    resolve({ plainlyProjectId: data.output.success.plainlyProjectId, compositions });

                    return;
                }

                case ciApi.RESOURCE_MANAGER_STATUS.FAILED:
                default: {
                    reject(new Error(parseErrorMessage(data.output.fail)));
                }
            }
        }

        function parseErrorMessage(failData: string | Error): string {
            if (!failData) {
                return "Failed to create the Plainly project";
            }

            const jsonPart = typeof failData === "string" ? (failData.split("with: ")[1] ?? "{}") : "{}";
            let parsed: { error?: { Cause?: string } };

            try {
                parsed = JSON.parse(jsonPart) as { error?: { Cause?: string } };
            } catch {
                parsed = {};
            }

            const causeString = parsed.error?.Cause ?? "{}";
            let cause: { errorMessage?: string };

            try {
                cause = JSON.parse(causeString) as { errorMessage?: string };
            } catch {
                cause = { errorMessage: causeString };
            }

            let details: { message?: string; params?: Record<string, unknown> };

            try {
                details = JSON.parse(cause.errorMessage ?? "{}") as {
                    message?: string;
                    params?: Record<string, unknown>;
                };
            } catch {
                details = { message: cause.errorMessage ?? "" };
            }
            const message = details.message ?? cause.errorMessage ?? "";
            const params = details.params ?? {};

            const missingFonts = Array.isArray(params.missingFonts) ? params.missingFonts.join(", ") : "";
            const missingFiles = Array.isArray(params.missingFiles) ? params.missingFiles.join(", ") : "";

            const extras = [
                missingFonts ? `Missing fonts: ${missingFonts}` : "",
                missingFiles ? `Missing files: ${missingFiles}` : ""
            ].filter(Boolean);

            return [message, ...extras].join("\n");
        }

        void pollApi();
    });
}
